import { getDenormalizedAmount } from 'src/common/apollo/utils/tokens'
import { DotcEnums } from 'src/common/contracts/enums/dotc'
import { ZERO } from 'src/common/utils/big-utils'
import {
  AssetPrice,
  MakeDynamicOfferParams,
  MakeOfferParams,
  MakeStaticOfferParams,
} from './types'

const _buildMakeOfferParams = ({
  depositAsset,
  withdrawalAsset,
  takingOfferType,
  offerPrice,
  offerOptions,
}: MakeOfferParams) => {
  const depositAssetPrice = depositAsset.assetPrice
  const withdrawalAssetPrice = withdrawalAsset.assetPrice

  const depositAssetAmount = BigInt(
    getDenormalizedAmount(depositAsset.amount, depositAsset.decimals)
  )
  const withdrawalAssetAmount = BigInt(
    getDenormalizedAmount(withdrawalAsset.amount, withdrawalAsset.decimals)
  )

  const percentage = BigInt(
    getDenormalizedAmount(offerPrice.percentage || 0, 2)
  )

  const _takingOfferType =
    depositAsset.type !== DotcEnums.AssetType.ERC20 ||
    withdrawalAsset.type !== DotcEnums.AssetType.ERC20
      ? DotcEnums.TakingOfferType.BlockOffer
      : takingOfferType

  const {
    specialAddresses,
    authorizationAddresses,
    expiryTimestamp,
    timelockPeriod,
    terms,
    commsLink,
  } = offerOptions

  return {
    depositAsset: {
      assetType: depositAsset.type,
      assetAddress: depositAsset.address,
      amount: depositAssetAmount,
      tokenId: BigInt(depositAsset.tokenId || 0),
      assetPrice: {
        priceFeedAddress: depositAssetPrice.priceFeedAddress,
        offerMinimumPrice: BigInt(
          depositAssetPrice.offerMinimumPrice.toString()
        ),
        offerMaximumPrice: BigInt(
          depositAssetPrice.offerMaximumPrice.toString()
        ),
      },
    },
    withdrawalAsset: {
      assetType: withdrawalAsset.type,
      assetAddress: withdrawalAsset.address,
      amount: withdrawalAssetAmount,
      tokenId: BigInt(withdrawalAsset.tokenId || 0),
      assetPrice: {
        priceFeedAddress: withdrawalAssetPrice.priceFeedAddress,
        offerMinimumPrice: BigInt(
          withdrawalAssetPrice.offerMinimumPrice.toString()
        ),
        offerMaximumPrice: BigInt(
          withdrawalAssetPrice.offerMaximumPrice.toString()
        ),
      },
    },
    offer: {
      takingOfferType: _takingOfferType,
      offerPrice: {
        offerPricingType: offerPrice.offerPricingType,
        percentageType: offerPrice.percentageType,
        unitPrice: BigInt(offerPrice.unitPrice.toString()), // TO DO: UPDATE CONSIDERING DECIMAL PLACES
        percentage,
      },
      specialAddresses,
      authorizationAddresses,
      expiryTimestamp: BigInt(expiryTimestamp),
      timelockPeriod: BigInt(timelockPeriod),
      terms,
      commsLink,
    },
  }
}

export const buildMakeStaticOfferParams = ({
  depositAsset,
  withdrawalAsset,
  takingOfferType,
  offerOptions,
}: MakeStaticOfferParams) => {
  return _buildMakeOfferParams({
    depositAsset: {
      ...depositAsset,
      assetPrice: {
        priceFeedAddress: '', // not needed
        offerMaximumPrice: ZERO, // not needed
        offerMinimumPrice: ZERO, // not needed
      },
    },
    withdrawalAsset: {
      ...withdrawalAsset,
      assetPrice: {
        priceFeedAddress: '', // not needed
        offerMaximumPrice: ZERO, // not needed
        offerMinimumPrice: ZERO, // not needed
      },
    },
    takingOfferType,
    offerPrice: {
      offerPricingType: DotcEnums.OfferPricingType.FixedPricing,
      unitPrice: ZERO, // not needed
      percentage: 0, // not needed
      percentageType: DotcEnums.PercentageType.NoType,
    },
    offerOptions,
  })
}

export const buildMakeDynamicOfferParams = ({
  depositAsset,
  withdrawalAsset,
  depositAssetPrice,
  withdrawalAssetPrice,
  takingOfferType,
  offerPrice,
  offerOptions,
}: MakeDynamicOfferParams) => {
  // Set default prices
  const _depositAssetPrice: AssetPrice = {
    priceFeedAddress: depositAssetPrice.priceFeedAddress,
    offerMinimumPrice: ZERO,
    offerMaximumPrice: ZERO,
  }

  const _withdrawalAssetPrice: AssetPrice = {
    priceFeedAddress: withdrawalAssetPrice.priceFeedAddress,
    offerMinimumPrice: ZERO,
    offerMaximumPrice: ZERO,
  }

  // Return the built params
  return _buildMakeOfferParams({
    depositAsset: {
      ...depositAsset,
      assetPrice: _depositAssetPrice,
    },
    withdrawalAsset: {
      ...withdrawalAsset,
      amount: ZERO.plus(1),
      assetPrice: _withdrawalAssetPrice,
    },
    takingOfferType,
    offerPrice: {
      offerPricingType: DotcEnums.OfferPricingType.DynamicPricing,
      unitPrice: ZERO,
      percentage: offerPrice.percentage,
      percentageType: offerPrice.percentageType,
    },
    offerOptions,
  })
}
